import { useMemo, useState } from 'react';
import { goBack, navigate, resetNav } from '../../app/navigation';
import { displayAddress, isInspectionLocked, type V2Area } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import type { IconName } from '../../ui/icons';
import { aiJobsStore, jobCountsOf } from '../../state/aiJobsStore';
import { areaFocusStore, resumeAreaIdFor } from '../../state/areaFocus';
import { buildCloseout, rowFactsFrom } from '../../state/closeout';
import { buildPreDepartureCheck } from '../../state/preDeparture';
import { aiStore } from '../../state/aiStore';
import { advancedSummariesOf } from '../../state/rowFacts';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { findingStore } from '../../state/findingStore';
import { inspectionProgress, inspectionStore, nextUninspectedItem } from '../../state/inspectionStore';
import { inspectionIssueCount } from '../../state/issueCounts';
import { buildPhotoPool, poolCounts } from '../../state/photoPool';
import { Banner, Button, Card, EmptyState, Icon, IconButton, ProgressBar, TopBar } from '../../ui/components';
import { formatDate } from '../../ui/hooks';
import { AreaGroups } from '../areas/AreaListScreen';
import { AreaOptionsSheet } from '../areas/AreaOptionsSheet';
import { clientOf } from '../inspections/inspectionSummary';

// Inspection Overview — Figma "04 — Inspection Overview" / 中文「02 检查总览」(iPhone design frozen
// 2026-09-10; Authority §6.5; inside the 验屋 tab). It answers "what is still uninspected?": one
// progress card (items inspected · % · findings / unable / uninspected) → Resume → the floor-grouped
// area cards ("n 项中已完成 m 项", defects badge, done check) → closeout · report status · photo
// pool · details. No dashboard, no charts. Locked (finalized) inspections are shown read-only.
export function InspectionShell(props: { inspectionId: string }) {
  const { t, tp } = useT();
  const state = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const evidenceState = useStore(evidenceStore, (s) => s);
  const inspection = state.inspections.find((i) => i.id === props.inspectionId) ?? null;
  const progress = useMemo(() => inspectionProgress(state, props.inspectionId), [state, props.inspectionId]);
  // B2 (device walkthrough 2026-10-27): the same number as the area cards below and Home (src/state/issueCounts.ts).
  const findingsCount = useMemo(() => inspectionIssueCount(state, findingState, props.inspectionId), [state, findingState, props.inspectionId]);
  const next = useMemo(() => nextUninspectedItem(state, props.inspectionId), [state, props.inspectionId]);
  const focus = useStore(areaFocusStore, (s) => s);
  // Where Resume goes and which area card is outlined: the area the inspector last had open (device
  // memory, src/state/areaFocus.ts) while it is still enabled and not completed; otherwise the area
  // of the next uninspected item in inspection order. Owner device test 2026-09-12: coming back into
  // an inspection must land where the work is, not on the first area of the house.
  const resumeAreaId = useMemo(() => resumeAreaIdFor(focus, state, props.inspectionId, next?.areaId ?? null), [focus, state, props.inspectionId, next]);
  const pool = useMemo(() => poolCounts(buildPhotoPool(props.inspectionId, evidenceState, state, findingState)), [props.inspectionId, evidenceState, state, findingState]);
  const aiState = useStore(aiStore, (s) => s);
  const jobs = useStore(aiJobsStore, (s) => s.jobs);
  const closeout = useMemo(() => buildCloseout(props.inspectionId, state, findingState, rowFactsFrom(aiState, evidenceState, advancedSummariesOf(jobs))), [props.inspectionId, state, findingState, aiState, evidenceState, jobs]);
  // 2026-10-27: the closeout's 离开前再看一眼 count, shown beside the blocking count so the inspector sees it
  // before leaving without opening the closeout. Advisory — it never changes the blocking count or the ready state.
  const departure = useMemo(
    () => buildPreDepartureCheck(props.inspectionId, { inspection: state, ai: aiState, jobs, evidence: evidenceState, findings: findingState }),
    [props.inspectionId, state, aiState, jobs, evidenceState, findingState]
  );
  const [options, setOptions] = useState<V2Area | null>(null);
  // Advanced AI jobs of this inspection (2026-09-13 §12): the row states how many are running and unread.
  const aiJobs = useStore(aiJobsStore, (s) => s.jobs);
  const aiCounts = useMemo(() => jobCountsOf({ jobs: aiJobs }, props.inspectionId), [aiJobs, props.inspectionId]);

  if (!inspection) {
    return (
      <div className="screen">
        <TopBar title={t('inspection.title')} onBack={() => resetNav({ screen: 'inspectionList' })} />
        <EmptyState
          title={t('inspection.notFoundTitle')}
          body={t('inspection.notFoundBody')}
          action={<Button onClick={() => resetNav({ screen: 'inspectionList' })}>{t('inspection.backToInspections')}</Button>}
        />
      </div>
    );
  }

  const client = clientOf(inspection, t);
  const pct = progress.total > 0 ? Math.round((progress.settled / progress.total) * 100) : 0;
  const locked = isInspectionLocked(inspection);
  const enabledAreas = state.areas.filter((a) => a.inspectionId === props.inspectionId && a.enabled).length;

  function resume() {
    if (!resumeAreaId) return;
    // Inside the area being resumed, the next uninspected item still opens (D-06); in a remembered
    // area whose next item lies elsewhere, the workspace opens as the inspector left it.
    const itemSheetId = next && next.areaId === resumeAreaId ? next.id : null;
    navigate({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: resumeAreaId, itemSheetId });
  }

  const links: Array<{ key: string; icon: IconName; label: string; meta?: string; ready?: boolean; onClick: () => void; testId: string }> = [
    {
      key: 'closeout',
      icon: 'task_alt',
      label: closeout.blocking.length > 0 ? t('closeout.openWithGaps', { n: closeout.blocking.length }) : t('closeout.open'),
      meta: !locked && departure.total > 0 ? t('closeout.leaveCount', { n: departure.total }) : undefined,
      ready: closeout.blocking.length === 0 && !locked,
      onClick: () => navigate({ screen: 'closeout', inspectionId: props.inspectionId }),
      testId: 'open-closeout'
    },
    { key: 'report', icon: 'receipt_long', label: t('overview.reportStatus'), onClick: () => navigate({ screen: 'report', inspectionId: props.inspectionId }), testId: 'open-report' },
    {
      key: 'aiTasks',
      icon: 'psychology',
      label: t('aiTasks.title'),
      meta: aiCounts.active + aiCounts.unread + aiCounts.failed + aiCounts.viewed > 0 ? t('aiTasks.counts', { active: aiCounts.active, unread: aiCounts.unread }) : undefined,
      ready: aiCounts.unread > 0,
      onClick: () => navigate({ screen: 'aiTasks', inspectionId: props.inspectionId }),
      testId: 'open-ai-tasks'
    },
    { key: 'pool', icon: 'perm_media', label: t('pool.title'), meta: tp('overview.photosCount', pool.all), onClick: () => navigate({ screen: 'photoPool', inspectionId: props.inspectionId }), testId: 'chip-pool' },
    { key: 'details', icon: 'description', label: t('overview.details'), onClick: () => navigate({ screen: 'inspectionDetails', inspectionId: props.inspectionId }), testId: 'open-details' }
  ];

  return (
    <div className="screen" data-testid="inspection-overview">
      <TopBar title={displayAddress(inspection, t('inspection.noAddress'))} subtitle={`${client} · ${formatDate(inspection.inspectionAt)}`} onBack={goBack} />
      <div className="screen-body">
        {locked ? (
          <div className="banner-slot">
            <Banner
              tone="info"
              icon="lock"
              title={t('report.lockedTitle')}
              body={t('report.lockedBody')}
              testId="overview-locked"
              action={
                <Button variant="text" size="small" block={false} onClick={() => navigate({ screen: 'report', inspectionId: props.inspectionId })} testId="overview-open-report">
                  {t('nav.reports')}
                </Button>
              }
            />
          </div>
        ) : null}
        {!locked && inspection.startMode === 'import' && pool.unlinked > 0 ? (
          <div className="banner-slot">
            <Banner
              tone="warning"
              icon="perm_media"
              title={tp('pool.unlinkedBanner', pool.unlinked)}
              body={t('pool.unlinkedBannerBody')}
              testId="overview-pool-banner"
              action={
                <Button variant="text" size="small" block={false} onClick={() => navigate({ screen: 'photoPool', inspectionId: props.inspectionId })} testId="overview-open-pool">
                  {t('pool.title')}
                </Button>
              }
            />
          </div>
        ) : null}

        <Card className="ov-progress" testId="overview-progress">
          <div className="ov-progress-head">
            <h2 className="ov-progress-title">{t('overview.progress')}</h2>
            <span className="ov-progress-count">{tp('common.itemsInspected', progress.total, { done: progress.settled })}</span>
          </div>
          <div className="ov-progress-bar">
            <ProgressBar value={progress.settled} total={progress.total} />
            <span className="ov-pct" data-testid="overview-pct">
              {t('common.percent', { n: pct })}
            </span>
          </div>
          <div className="ov-stats" data-testid="overview-chips">
            <button type="button" className="ov-stat ov-stat-findings" onClick={() => navigate({ screen: 'findings', inspectionId: props.inspectionId })} data-testid="chip-findings">
              <strong>{findingsCount}</strong>
              <span>{tp('overview.chipFindings', findingsCount)}</span>
            </button>
            <div className="ov-stat ov-stat-unable" data-testid="chip-unable">
              <strong>{progress.unable}</strong>
              <span>{t('overview.chipUnable')}</span>
            </div>
            <div className="ov-stat ov-stat-remaining" data-testid="chip-uninspected">
              <strong>{progress.notChecked}</strong>
              <span>{t('overview.chipUninspected')}</span>
            </div>
          </div>
        </Card>

        <div className="screen-cta">
          <Button trailingIcon="arrow_forward" onClick={resume} disabled={!resumeAreaId || locked} testId="resume-inspection">
            {resumeAreaId ? t('overview.resume') : t('overview.allInspected')}
          </Button>
        </div>

        <div className="ov-section-head">
          <div>
            <h2 className="ov-section-title">{t('overview.areas')}</h2>
            <p className="section-meta">{tp('common.areasCount', enabledAreas)}</p>
          </div>
          <IconButton icon="edit" label={t('layout.edit')} onClick={() => navigate({ screen: 'propertyLayout', inspectionId: props.inspectionId })} disabled={locked} tone="tonal" testId="edit-layout" />
        </div>
        <AreaGroups inspectionId={props.inspectionId} mode="inspect" onOptions={setOptions} currentAreaId={resumeAreaId} />

        <div className="ov-links">
          {links.map((link) => (
            <button key={link.key} type="button" className={`ov-link ${link.ready ? 'ov-link-ready' : ''}`} onClick={link.onClick} data-testid={link.testId}>
              <Icon name={link.icon} />
              <span className="ov-link-label">{link.label}</span>
              {link.meta ? <span className="ov-link-meta">{link.meta}</span> : null}
              <Icon name="chevron_right" className="ov-link-chevron" />
            </button>
          ))}
        </div>
      </div>
      {options ? <AreaOptionsSheet area={options} onClose={() => setOptions(null)} /> : null}
    </div>
  );
}
